import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SpecificationReviewNotificationService } from './../service/specification-review-notification.service';

import { AlertService } from '@core/ui-kit/services/alert.service';
import { catchError, filter, groupBy, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { SpecificationReviewItemStatus } from '../enums/specification-review-item-status.enum';
import { SpecificationReviewService } from '../service/specification-review.service';
import { loadSpecificationReviewActions, updateSpecificationReviewActions } from './specification-review.actions';

@Injectable()
export class SpecificationReviewEffects {
  private readonly actions$ = inject(Actions);
  private readonly specificationReviewService = inject(SpecificationReviewService);
  private readonly specificationReviewNotificationService = inject(SpecificationReviewNotificationService);
  private readonly alertService = inject(AlertService);

  generateSpecificationReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.generateSpecificationReview),
      switchMap(({ bundleId }) =>
        this.specificationReviewService.generateSpecificationReviewItems(bundleId).pipe(
          map(() =>
            loadSpecificationReviewActions.loadSpecificationReviewItems({
              bundleId,
            }),
          ),
        ),
      ),
    ),
  );

  deleteSpecificationReviewItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.deleteSpecificationReviewItem),
      switchMap(({ bundleId, itemId, page, query }) =>
        this.specificationReviewService.deleteItem(bundleId, itemId).pipe(
          map(() =>
            loadSpecificationReviewActions.deleteSpecificationReviewItemSuccess({
              itemId,
              bundleId,
              page,
              query,
            }),
          ),
          catchError((error) =>
            of(loadSpecificationReviewActions.deleteSpecificationReviewItemFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  /* deleteSpecificationReviewItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.deleteSpecificationReviewItem),
      tap((action) => console.log('[EFFECT DEBUG] Azione delete ricevuta:', action)), // LOG 1
      switchMap(({ bundleId, itemId, page, query }) =>
        this.specificationReviewService.deleteItem(bundleId, itemId).pipe(
          tap((responseFromService) =>
            console.log('[EFFECT DEBUG] Risposta dal servizio ricevuta:', responseFromService),
          ), // LOG 2
          map(() => {
            const successAction = loadSpecificationReviewActions.deleteSpecificationReviewItemSuccess({
              itemId,
              bundleId,
              page,
              query,
            });
            console.log('[EFFECT DEBUG] Dispatching azione di SUCCESSO:', successAction); // LOG 3
            return successAction;
          }),
          catchError((error) => {
            console.error('[EFFECT DEBUG] ERRORE nel servizio catturato:', error); // LOG 4
            const failureAction = loadSpecificationReviewActions.deleteSpecificationReviewItemFailure({
              error: error.message,
            });
            console.log('[EFFECT DEBUG] Dispatching azione di FALLIMENTO:', failureAction); // LOG 5
            return of(failureAction);
          }),
        ),
      ),
    ),
  ); */
  reloadAfterDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.deleteSpecificationReviewItemSuccess),
      map(({ bundleId, page, query }) =>
        loadSpecificationReviewActions.loadSpecificationReviewItems({
          bundleId,
          page,
          query,
        }),
      ),
    ),
  );

  saveSpecificationReviewItemDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSpecificationReviewActions.saveSpecificationReviewItemDescription),
      switchMap(({ bundleId, itemId, text, contentType, page, query }) =>
        this.specificationReviewService
          .updateSpecificationReviewItemDescription(bundleId, itemId, text, contentType)
          .pipe(
            map(() =>
              loadSpecificationReviewActions.loadSpecificationReviewItems({
                bundleId,
                page,
                query,
              }),
            ),
          ),
      ),
    ),
  );

  createSpecificationReviewItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.createSpecificationReviewItem),
      switchMap(({ bundleId, payload }) =>
        this.specificationReviewService.createItem(bundleId, payload).pipe(
          map((item) => loadSpecificationReviewActions.createSpecificationReviewItemSuccess({ item })),
          catchError((error) =>
            of(loadSpecificationReviewActions.createSpecificationReviewItemFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  valiadateSpecificationReviewItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSpecificationReviewActions.valiadateSpecificationReviewItem),
      switchMap(({ bundleId, itemId, page, query }) =>
        this.specificationReviewService.validateItem(bundleId, itemId).pipe(
          map(() =>
            loadSpecificationReviewActions.loadSpecificationReviewItems({
              bundleId,
              page,
              query,
            }),
          ),
          catchError(() => {
            return of(
              updateSpecificationReviewActions.valiadateSpecificationReviewFailure({
                itemId,
              }),
            );
          }),
        ),
      ),
      tap(() => this.alertService.showOperationCompletedAlert()),
    ),
  );

  loadSpecificationReviewItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.loadSpecificationReviewItems),
      switchMap(({ bundleId, page, pageSize, query }) =>
        this.specificationReviewService
          .getSpecificationReviewItems(bundleId, page, pageSize, query)
          .pipe(map((items) => loadSpecificationReviewActions.loadSpecificationReviewItemsSuccess({ items }))),
      ),
    ),
  );

  pollSpecificationReviewItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSpecificationReviewActions.loadSpecificationReviewItemsSuccess),
      filter(({ items }) => items?.status === SpecificationReviewItemStatus.GENERATING),
      groupBy(({ items }) => items!.bundleId),
      mergeMap((group$) =>
        group$.pipe(
          take(1),
          switchMap(({ items }) =>
            this.specificationReviewNotificationService.pollSpecificationReviewItems(items!.bundleId).pipe(
              map((finalItems) =>
                loadSpecificationReviewActions.loadSpecificationReviewItemsSuccess({ items: finalItems }),
              ),
              catchError((error) => {
                if (error.status === 400 && error.error.message === 'NOT_FOUND') {
                  return of(loadSpecificationReviewActions.loadSpecificationReviewItemsSuccess({ items: null }));
                }
                return of(
                  loadSpecificationReviewActions.loadSpecificationReviewItemsFailure({
                    error: error.error.message,
                  }),
                );
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
